import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useGetVisitGPProfileQuery, useUpdateUserMutation } from "../userApi";
import { useGetTimelinePostsQuery } from "../../../features/timeline/timelineApiSlice";
import { SuccessNotify, FailedNotify } from "../../../constants/tostify";
import { decodeId } from "../../../utils/idObfuscator";

const mapShareEntry = (entry) => {
  const post = entry?.postid;
  if (!post || typeof post !== "object" || !post._id) return null;

  const senderBT = String(entry?.senderBusinessType || "")
    .toLowerCase()
    .replace(/[\s_-]+/g, "");
  const senderShop =
    entry?.sharebyshopid && typeof entry.sharebyshopid === "object"
      ? entry.sharebyshopid
      : null;
  const sharedAsPerson =
    !entry?.sharebyshopid &&
    (senderBT === "generalpublic" || senderBT === "");
  const shopLabel =
    senderShop?.shopname ||
    senderShop?.profileName ||
    senderShop?.companyname ||
    null;
  const senderUser =
    entry?.sharebyshopuserid && typeof entry.sharebyshopuserid === "object"
      ? entry.sharebyshopuserid
      : null;

  const authorName =
    (!sharedAsPerson && shopLabel) ||
    senderUser?.fullname ||
    null;

  return {
    ...post,
    // Shares from someone's /user-profile always belong on General Public,
    // even if an older row was stamped with a shop businessType.
    businessType: sharedAsPerson
      ? "generalPublic"
      : entry.businessType || post.businessType || "generalPublic",
    _isShared: true,
    _sharedFrom: authorName
      ? {
          fullname: authorName,
          name: authorName,
          imageUrl:
            (!sharedAsPerson &&
              (senderShop?.image || senderShop?.imageUrl)) ||
            senderUser?.imageUrl ||
            null,
          _isShop: !sharedAsPerson && !!shopLabel,
        }
      : entry.sharebyshopuserid || null,
    createdAt: entry.createdAt || post.createdAt,
  };
};

export const useOtherUserProfile = () => {
  const { id: obfuscatedId } = useParams();
  const id = decodeId(obfuscatedId);
  const { name } = useSelector((state) => state.user);
  const authUser = useSelector((state) => state.auth?.user);
  const viewerId = name?._id || authUser?._id;
  /* ------- Layout State ------ */
  const [leftSide, setLeftSide] = useState(7);
  const [rightSide, setRightSide] = useState(5);

  /* ------ Profile State ------- */
  const [checkconnect, setCheckconnect] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState("Connect");
  const [posts, setPosts] = useState([]);
  const [createShop, setCreateShop] = useState({ text: "", privacy: "" });

  const {
    data: visitData,
    isLoading: isVisitLoading,
    refetch: refetchVisit,
  } = useGetVisitGPProfileQuery(
    { userId: id, viewerId },
    { skip: !id || !viewerId, refetchOnMountOrArgChange: true },
  );

  // Same source as User Profile Timeline — sharebyother lives here (includes
  // merged shop-timeline shares from getAll when no shopid).
  const {
    data: timelineRes,
    isLoading: isTimelineLoading,
    refetch: refetchTimeline,
  } = useGetTimelinePostsQuery(
    { userid: id, businessType: "generalPublic" },
    {
      skip: !id,
      refetchOnMountOrArgChange: true,
    },
  );

  const [updateUser, { isLoading: isUpdating }] = useUpdateUserMutation();

  /* -------- Derived data ------- */
  const visitPayload = useMemo(() => visitData?.data ?? visitData, [visitData]);

  const infooo = useMemo(() => visitPayload?.User ?? null, [visitPayload]);

  useEffect(() => {
    if (!visitPayload) return;
    setCheckconnect(visitPayload?.connected ?? false);
    setConnectionStatus(visitPayload?.connectionStatus ?? "Connect");
  }, [visitPayload]);

  useEffect(() => {
    const fromTimeline = timelineRes?.data?.[0] || null;
    const fromVisit = visitPayload?.timelineEntry || null;

    const ownPosts = Array.isArray(fromTimeline?.my)
      ? fromTimeline.my
      : Array.isArray(fromVisit?.my)
        ? fromVisit.my
        : [];

    // Received shares + shares this user sent (so /other-profile shows the
    // full share history, not only inbox rows).
    const shareEntries = [
      ...(Array.isArray(fromVisit?.sharebyother) ? fromVisit.sharebyother : []),
      ...(Array.isArray(fromTimeline?.sharebyother)
        ? fromTimeline.sharebyother
        : []),
      ...(Array.isArray(fromVisit?.sharedtoother)
        ? fromVisit.sharedtoother
        : []),
      ...(Array.isArray(fromTimeline?.sharedtoother)
        ? fromTimeline.sharedtoother
        : []),
    ];

    // Connected /other-profile must show ALL shares delivered to this user
    // (personal GP + shop BT rows merged from their shops) and outbound.
    const sharedPosts = shareEntries
      .filter((entry) => {
        const post = entry?.postid;
        return post && typeof post === "object" && post._id;
      })
      .map(mapShareEntry)
      .filter(Boolean);

    const seen = new Set();
    const merged = [...sharedPosts, ...ownPosts]
      .filter((p) => {
        const pid = p?._id?.toString?.() || p?._id;
        if (!pid || seen.has(pid)) return false;
        seen.add(pid);
        return true;
      })
      .sort(
        (a, b) =>
          new Date(b?.createdAt || 0).getTime() -
          new Date(a?.createdAt || 0).getTime(),
      );

    setPosts(merged);
  }, [timelineRes, visitPayload]);

  const inputChange = useCallback((e) => {
    const { name: field, value } = e.target;
    setCreateShop((prev) => ({ ...prev, [field]: value }));
  }, []);

  const userDatahandler = useCallback(async () => {
    if (!name?._id) return;
    try {
      const payload = { id: name._id, data: { description: createShop.text } };
      const result = await updateUser(payload).unwrap();
      if (result?.success) {
        SuccessNotify("Successfully!");
        refetchVisit();
        refetchTimeline();
      }
    } catch {
      FailedNotify("Failed! Try Again");
    }
  }, [name?._id, createShop.text, updateUser, refetchVisit, refetchTimeline]);

  const widthHandler = useCallback(() => {
    const isExpanded = leftSide === 7 && rightSide === 5;
    setLeftSide(isExpanded ? 2 : 7);
    setRightSide(isExpanded ? 10 : 5);
  }, [leftSide, rightSide]);

  return {
    id,
    name,
    infooo,
    checkconnect,
    connectionStatus,
    posts,
    createShop,
    setCreateShop,
    inputChange,
    userDatahandler,
    isVisitLoading:
      (isVisitLoading && !visitData) || (isTimelineLoading && !timelineRes),
    isUpdating,
    refetchVisit: () => {
      refetchVisit();
      refetchTimeline();
    },
    leftSide,
    rightSide,
    widthHandler,
  };
};
